/**
 * Every user-facing string in one place.
 *
 * Groundwork for real localization: today there is one locale (fa), so this is
 * a flat object rather than an i18n runtime. What it buys now is that no
 * component invents its own wording, and that swapping in a second locale later
 * is a change to this file plus a lookup helper — not a sweep through every
 * screen. Formatting of dates and digits stays in `format.js`.
 */
export const S = {
  app: {
    name: 'مش‌هاب',
    loading: 'در حال بارگذاری…',
    retry: 'تلاش دوباره',
    cancel: 'انصراف',
    save: 'ذخیره',
    create: 'ایجاد',
    edit: 'ویرایش',
    close: 'بستن',
    confirm: 'تأیید',
    search: 'جست‌وجو',
    none: '—',
    yes: 'بله',
    no: 'خیر',
    active: 'فعال',
    inactive: 'غیرفعال',
    activate: 'فعال‌سازی',
    deactivate: 'غیرفعال‌سازی',
    system: 'سیستمی',
    notFound: 'موردی یافت نشد',
    unknownError: 'خطای نامشخص',
    add: 'افزودن',
    remove: 'حذف',
    delete: 'حذف',
    restore: 'بازگردانی',
    all: 'همه',
    from: 'از',
    to: 'تا',
    apply: 'اعمال',
    clear: 'پاک کردن',
    optional: 'اختیاری',
    saving: 'در حال ذخیره…',
    saved: 'ذخیره شد',
    previous: 'قبلی',
    next: 'بعدی',
    pageOf: (page, total) => `صفحه ${page} از ${total}`,
    download: 'دریافت',
    // --- the date and time pickers ---
    pickDate: 'انتخاب تاریخ',
    pickTime: 'انتخاب ساعت',
    today: 'امروز',
    hour: 'ساعت',
    minute: 'دقیقه',
    previousMonth: 'ماه قبل',
    nextMonth: 'ماه بعد',
  },

  /**
   * The inline vocabulary creator (`components/VocabularyField.web.jsx`).
   * Deliberately generic: it is used for activity types, interaction types and
   * issue types, and naming any of them here would put a deployment's
   * vocabulary into the client (CLAUDE.md §1).
   */
  vocabulary: {
    none: '— انتخاب کنید —',
    addNew: 'افزودن گزینه تازه به این فهرست',
    addNewShort: 'گزینه تازه',
    newLabel: 'نام گزینه تازه',
    newHint: 'نام را بنویسید؛ همین‌جا افزوده و انتخاب می‌شود.',
    codeIs: (code) => `کد: ${code} — پس از ایجاد قابل تغییر نیست.`,
    emptyUser: 'هنوز گزینه‌ای تعریف نشده است. از مدیر سامانه بخواهید این فهرست را تکمیل کند.',
  },
  auth: {
    signIn: 'ورود به مش‌هاب',
    signInAction: 'ورود با حساب سازمانی',
    signInHint: 'برای ادامه با حساب سازمانی خود وارد شوید.',
    signOut: 'خروج',
    signingIn: 'در حال ورود…',
    sessionExpired: 'نشست شما منقضی شده است. دوباره وارد شوید.',
    forbiddenTitle: 'دسترسی ندارید',
    forbiddenHint:
      'این بخش تنها برای مدیران سامانه در دسترس است. اگر فکر می‌کنید اشتباهی رخ داده، با مدیر سامانه تماس بگیرید.',
    backToContacts: 'بازگشت به مخاطبین',
    roleAdmin: 'مدیر',
    roleUser: 'کاربر',
  },

  nav: {
    dashboard: 'داشبورد',
    contacts: 'مخاطبین',
    settings: 'تنظیمات',
    shortcuts: 'میان‌برها',
    // The other bundle of the app, at /platform (ex Parsina's admin panel).
    platform: 'پنل پلتفرم',
    // …which is all of it a CRM-only user may open: the demo-request queue.
    demoRequests: 'درخواست‌های دمو',
  },

  contacts: {
    title: 'مخاطبین',
    count: (n) => `${n} مورد`,
    searchPlaceholder: 'جست‌وجوی نام یا شماره…',
    searchLabel: 'جست‌وجوی مخاطبین',
    emptyHint: 'عبارت جست‌وجو را تغییر دهید.',
    columns: {
      name: 'نام',
      specialty: 'تخصص / حوزه',
      primaryPhone: 'شماره اصلی',
      channels: 'کانال‌ها',
      assessment: 'ارزیابی',
      referrer: 'معرف',
      tags: 'برچسب‌ها',
      updatedAt: 'آخرین بروزرسانی',
    },
    columnPhoto: 'تصویر',
    statusFilterAll: 'همه وضعیت‌ها',
    showDeleted: 'حذف‌شده‌ها',
    deletedNotice: 'این مخاطب حذف شده است و در فهرست‌ها دیده نمی‌شود.',
    detail: {
      profile: 'مشخصات',
      phones: 'شماره‌ها',
      interests: 'محصولات مورد علاقه',
      affiliations: 'وابستگی‌ها',
      interactions: 'تعاملات',
      statusHistory: 'تاریخچه مرحله اشتراک',
      customFields: 'اطلاعات تکمیلی',
      noPhones: 'شماره‌ای ثبت نشده است.',
      noInteractions: 'تعاملی ثبت نشده است.',
      noInterests: 'محصولی انتخاب نشده است.',
      noAffiliations: 'وابستگی ثبت نشده است.',
      primary: 'اصلی',
      makePrimary: 'انتخاب به‌عنوان اصلی',
      outcome: 'نتیجه',
      kind: 'نوع',
      fieldOfWork: 'حوزه فعالیت',
      specialty: 'تخصص',
      subSpecialty: 'فوق تخصص',
      subscriptionType: 'نوع اشتراک',
      birthDate: 'تاریخ تولد',
      instagram: 'اینستاگرام',
      address: 'نشانی',
      description: 'توضیحات',
      notFound: 'مخاطب یافت نشد',
      firstName: 'نام',
      lastName: 'نام خانوادگی',
      orgName: 'نام سازمان',
      gender: 'جنسیت',
      tags: 'برچسب‌ها',
      addTag: 'افزودن برچسب',
      editProfile: 'ویرایش مشخصات',
      changeStatus: 'تغییر وضعیت',
      recordInteraction: 'ثبت تعامل',
      photo: 'تصویر مخاطب',
      // Read out by a screen reader in place of a photo, and shown on hover.
      // The glyph says something about the person, so unlike the initials it
      // is not decorative and needs a name.
      genderAvatar: { MALE: 'مخاطب مرد', FEMALE: 'مخاطب زن' },
      uploadPhoto: 'بارگذاری تصویر',
      replacePhoto: 'تغییر تصویر',
      deleteContact: 'حذف مخاطب',
      undeleteContact: 'بازگردانی مخاطب',
      confirmDelete: (name) =>
        `«${name}» حذف شود؟ از فهرست‌ها پنهان می‌شود اما سوابق، تعاملات و تاریخچه‌اش دست‌نخورده می‌مانند و مدیر می‌تواند بازش گرداند.`,
      confirmUndelete: (name) => `«${name}» به فهرست مخاطبین بازگردانده شود؟`,
      deleteReason: 'دلیل حذف',
      // A number a live contact already holds. The server names WHO; the
      // wording is here, and the link lets a rep open the one on file instead
      // of typing the doctor in twice.
      duplicatePhone: (number, name) =>
        `شمارهٔ ${number} قبلاً برای «${name || 'مخاطب دیگری'}» ثبت شده است؛ مخاطب تکراری ساخته نشد.`,
      duplicatePhoneSameContact: (number) =>
        `شمارهٔ ${number} قبلاً برای همین مخاطب ثبت شده است.`,
      openExistingContact: 'مشاهدهٔ همان مخاطب',
      addPhone: 'افزودن شماره',
      editPhone: 'ویرایش شماره',
      phoneNumber: 'شماره',
      phoneLabel: 'برچسب شماره',
      phoneNotes: 'یادداشت',
      channels: 'کانال‌ها',
      confirmRemovePhone: (number) => `شماره ${number} حذف شود؟ این حذف قطعی است.`,
      addAffiliation: 'افزودن وابستگی',
      organization: 'سازمان',
      person: 'شخص',
      roleTitle: 'سمت',
      searchOrganization: 'جست‌وجوی سازمان…',
      searchPerson: 'جست‌وجوی شخص…',
      confirmRemoveAffiliation: (name) => `وابستگی به «${name}» حذف شود؟`,

      // --- the CRM profile fields. Standard Persian CRM terms rather than
      // literal translations: «سرنخ» is what a sales team says, «رهبری» is
      // what a dictionary says. ---
      sections: {
        basics: 'اطلاعات پایه',
        professional: 'اطلاعات حرفه‌ای',
        crm: 'اطلاعات CRM',
        facility: 'اطلاعات مرکز',
        relations: 'ارتباطات',
      },
      // The spec's own title, and the one the filter panel uses — so the
      // field and the filter on it read the same.
      lifecycleStage: 'وضعیت چرخه عمر مشتری',
      salesStage: 'مرحله فروش',
      customerHealth: 'وضعیت مشتری',
      engagementStatus: 'وضعیت همکاری',
      // The pre-existing subscription pipeline. Relabelled so it cannot be
      // read as the new «وضعیت همکاری» beside it — same values, same history,
      // a name that says which question it answers.
      collaborationStatus: 'مرحله اشتراک',
      city: 'شهر',
      facilityType: 'نوع مخاطب',
      centerName: 'نام مرکز',
      centerNameRequired: 'برای این نوع مخاطب، ثبت نام مرکز الزامی است.',
      professionalCode: 'نظام پزشکی / کد حرفه‌ای',
      acquisitionSource: 'نحوه آشنایی',
      isGold: 'مشتری ویژه (Gold)',
      isGoldOn: 'ویژه',
      referrer: 'معرف',
      addReferrer: 'افزودن معرف',
      changeReferrer: 'تغییر معرف',
      removeReferrer: 'حذف معرف',
      searchContact: 'جست‌وجوی مخاطب…',
    moreContacts: (n) => `${n} مخاطب دیگر هم هست؛ برای یافتن آن‌ها جست‌وجو کنید.`,
      noReferrer: 'معرفی ثبت نشده است.',
      referrerSelfError: 'یک مخاطب نمی‌تواند معرف خودش باشد.',
      files: 'فایل‌های پیوست',
      addFile: 'افزودن فایل',
      noFiles: 'فایلی پیوست نشده است.',
    },
  },

  quickAdd: {
    title: 'ثبت سریع مخاطب',
    hint: 'فقط نام خانوادگی لازم است؛ باقی اطلاعات را بعداً کامل کنید.',
    open: 'ثبت سریع',
    firstName: 'نام',
    lastName: 'نام خانوادگی',
    phone: 'شماره تماس',
    fieldOfWork: 'حوزه فعالیت',
    note: 'یادداشت اولین تماس',
    notePlaceholder: 'کجا و چطور آشنا شدید؟ این متن به‌عنوان اولین تعامل ثبت می‌شود.',
    submit: 'ثبت و بستن',
    submitAndAnother: 'ثبت و مورد بعدی',
    added: (name) => `«${name}» ثبت شد.`,
    lastNameRequired: 'نام خانوادگی لازم است.',
    // Mobile only: the disclosure that hides the two fields a rep does not
    // type while talking.
    more: 'اطلاعات بیشتر',
    channels: 'راه‌های ارتباطی',
    noteBecomesInteraction: 'یادداشت به‌عنوان اولین تعامل ثبت می‌شود.',
    // The CRM fields, behind a disclosure that starts CLOSED. Exhibition mode
    // is a surname, a number and thirty seconds; everything below is for the
    // person filling a profile in at a desk.
    crmDetails: 'اطلاعات تکمیلی CRM',
    crmDetailsHint: 'اختیاری — بعداً هم از صفحه مخاطب قابل ویرایش است.',
    product: 'محصول مورد علاقه',
    sendMessage: 'ارسال پیام به این مخاطب',
  },

  interaction: {
    title: 'ثبت تعامل',
    type: 'نوع تعامل',
    occurredAt: 'زمان',
    date: 'تاریخ',
    time: 'ساعت',
    description: 'شرح',
    outcome: 'نتیجه',
    status: 'وضعیت',
    followUpNote: 'یادداشت پیگیری',
    followUpHint:
      'فعلاً به انتهای شرح افزوده می‌شود؛ پیگیری به‌عنوان یک فعالیت مستقل با سرویس زمان‌بندی می‌آید.',
    attachments: 'پیوست‌ها',
    dropzone: 'فایل را اینجا رها کنید یا برای انتخاب کلیک کنید',
    uploading: 'در حال بارگذاری…',
    uploadFailed: 'بارگذاری ناموفق بود.',
    filterType: 'نوع',
    filterFrom: 'از تاریخ',
    filterTo: 'تا تاریخ',
    clearFilters: 'حذف فیلترها',
    loadMore: 'نمایش بیشتر',
    by: 'ثبت توسط',
    invalidDate: 'تاریخ معتبر نیست (مثال: ۱۴۰۵/۰۶/۰۸).',
    typeRequired: 'نوع تعامل را انتخاب کنید.',
  },

  voice: {
    start: 'شروع گویش',
    stop: 'توقف گویش',
    listening: 'در حال شنیدن…',
    denied: 'دسترسی به میکروفون داده نشد. از تنظیمات مرورگر اجازه دهید.',
    failed: 'تبدیل گفتار به متن انجام نشد.',
    noSpeech: 'صدایی شنیده نشد.',
    privacy: 'گفتار در مرورگر شما پردازش می‌شود و روی سرورهای مش‌هاب ذخیره نمی‌گردد.',
  },

  status: {
    title: 'تغییر مرحله اشتراک',
    current: 'وضعیت فعلی',
    next: 'وضعیت جدید',
    reason: 'دلیل',
    reasonRequiredBackward:
      'برای بازگشت به مرحله قبل، ذکر دلیل لازم است — کسی که ماه‌ها بعد این تاریخچه را می‌خواند باید بداند چه شد.',
    reasonOptional: 'دلیل (اختیاری)',
    submit: 'ثبت تغییر',
    history: 'تاریخچه',
    by: 'توسط',
  },

  imports: {
    title: 'ورود مخاطبین از فایل اکسل',
    intro:
      'فایل اکسل همایش را انتخاب کنید. ابتدا پیش‌نمایش گرفته می‌شود و هیچ چیزی ذخیره نمی‌شود؛ ' +
      'پس از تأیید شما، همان نتیجه اعمال می‌گردد.',
    columns: 'ستون‌های مورد انتظار: مشخصات پزشکان، همایش/کنگره، تاریخ پرزنت، توضیحات، شماره تلفن',
    choose: 'انتخاب فایل',
    file: 'فایل',
    sheet: 'برگه',
    preview: 'پیش‌نمایش',
    commit: 'تأیید و ثبت انتخاب‌شده‌ها',
    committing: 'در حال ثبت…',
    previewing: 'در حال بررسی…',
    again: 'فایل دیگر',
    summary: 'خلاصه',
    created: 'مخاطب جدید',
    matched: 'مخاطب موجود',
    skipped: 'تکراری (نادیده)',
    invalid: 'نامعتبر',
    newSources: 'همایش‌های تازه که ساخته می‌شوند',
    newInteractionType: 'نوع تعامل تازه که ساخته می‌شود',
    rowsTitle: 'سطرها',
    row: 'سطر',
    outcome: 'نتیجه',
    detail: 'توضیح',
    warnings: 'هشدار',
    // The operator's own column headings, so the preview can be read against
    // the spreadsheet instead of trusted.
    cols: {
      name: 'مشخصات پزشکان',
      congress: 'همایش/کنگره',
      date: 'تاریخ پرزنت',
      description: 'توضیحات',
      phone: 'شماره تلفن',
    },
    readAs: 'خوانده شد',
    notRead: 'خوانده نشد',
    selectAll: 'انتخاب همه',
    selectRow: 'انتخاب سطر',
    selectedOf: '{n} از {total} سطر انتخاب شده',
    nothingSelected: 'هیچ سطری انتخاب نشده است.',
    notSelectable: 'قابل ورود نیست',
    selectionNote:
      'نتیجهٔ هر سطر برای کل فایل محاسبه شده است؛ با تغییر انتخاب، هنگام ثبت دوباره محاسبه می‌شود.',
    genders: { MALE: 'آقا', FEMALE: 'خانم' },
    outcomes: {
      CREATED: 'مخاطب جدید',
      MATCHED: 'به مخاطب موجود افزوده شد',
      SKIPPED: 'تکراری — نادیده گرفته شد',
      INVALID: 'نامعتبر',
    },
    done: 'ثبت انجام شد',
    previewOnly: 'این فقط پیش‌نمایش است — هنوز چیزی ذخیره نشده.',
    unmatchable: 'بدون شماره — در ورود مجدد تکرار می‌شود',
    onlyAdmin: 'فقط مدیر می‌تواند از فایل ورود انجام دهد.',
    empty: 'سطری برای نمایش نیست.',
  },

  settings: {
    title: 'تنظیمات',
    subtitle: 'پیکربندی سامانه — تنها برای مدیران',
    sections: {
      lookups: 'فهرست‌های پایه',
      specialties: 'تخصص‌ها',
      tags: 'برچسب‌ها',
      customFields: 'فیلدهای سفارشی',
      templates: 'قالب‌های پیام',
      activityTypes: 'انواع فعالیت',
      issueTypes: 'انواع مسئله',
      users: 'کاربران',
      import: 'ورود از اکسل',
    },
    common: {
      code: 'کد',
      labelFa: 'برچسب فارسی',
      labelEn: 'برچسب انگلیسی',
      sortOrder: 'ترتیب',
      status: 'وضعیت',
      actions: 'عملیات',
      moveUp: 'انتقال به بالا',
      moveDown: 'انتقال به پایین',
      codeImmutable: 'کد پس از ایجاد قابل تغییر نیست.',
      systemLocked: 'ردیف سیستمی: تنها برچسب‌ها قابل ویرایش‌اند.',
      noDelete:
        'حذف وجود ندارد؛ چون سوابق تاریخی به این مقادیر ارجاع می‌دهند، تنها غیرفعال‌سازی ممکن است.',
      confirmDeactivate: (name) =>
        `«${name}» غیرفعال شود؟ از فهرست‌های انتخاب حذف می‌شود اما روی سوابق قبلی باقی می‌ماند.`,
      confirmActivate: (name) => `«${name}» دوباره فعال شود؟`,
      showInactive: 'نمایش غیرفعال‌ها',
      newItem: 'مورد جدید',
    },
    lookups: {
      kindLabel: 'نوع فهرست',
      kinds: {
        FIELD_OF_WORK: 'حوزه فعالیت',
        SPECIALTY: 'تخصص',
        COMMUNICATION_CHANNEL: 'کانال ارتباطی',
        PRODUCT: 'محصول',
        INTERACTION_TYPE: 'نوع تعامل',
        TEMPLATE_CATEGORY: 'دسته‌بندی قالب پیام',
        ACQUISITION_SOURCE: 'نحوه آشنایی',
        LIFECYCLE_STAGE: 'وضعیت چرخه عمر مشتری',
        SALES_STAGE: 'مرحله فروش',
        CUSTOMER_HEALTH: 'وضعیت مشتری',
        ENGAGEMENT_STATUS: 'وضعیت همکاری',
        CITY: 'شهر',
        FACILITY_TYPE: 'نوع مخاطب',
      },
      createTitle: 'افزودن مورد تازه',
      codeHint: 'حروف بزرگ انگلیسی، اعداد و زیرخط — مثلاً NEW_PRODUCT',
      requiresDetail: 'نیازمند نام مرکز',
      requiresDetailHint:
        'با انتخاب این گزینه، ثبت نام مرکز برای مخاطب الزامی می‌شود. برای «نوع مخاطب» کاربرد دارد.',
    },
    activityTypes: {
      hint: 'انواعی که در تقویم و هنگام ثبت فعالیت انتخاب می‌شوند.',
      createTitle: 'نوع فعالیت تازه',
      empty: 'هیچ نوع فعالیتی تعریف نشده است؛ تا زمانی که یکی اضافه نشود، ثبت فعالیت در تقویم ممکن نیست.',
    },
    issueTypes: {
      hint: 'دسته‌بندی مسائل و شکایت‌هایی که از مشتریان ثبت می‌شود.',
      createTitle: 'نوع مسئله تازه',
      empty: 'هیچ نوع مسئله‌ای تعریف نشده است؛ تا زمانی که یکی اضافه نشود، ثبت مسئله ممکن نیست.',
    },
    specialties: {
      title: 'درخت تخصص‌ها',
      addChild: 'افزودن زیرشاخه',
      moveTo: 'انتقال به',
      makeRoot: 'انتقال به ریشه',
      hasActiveChildren: 'ابتدا زیرشاخه‌های فعال را غیرفعال کنید.',
      cycleRejected: 'این جابه‌جایی حلقه ایجاد می‌کند و مجاز نیست.',
      rootLevel: 'سطح ریشه',
    },
    tags: {
      name: 'نام برچسب',
      color: 'رنگ',
      preview: 'پیش‌نمایش',
      createTitle: 'برچسب تازه',
      isAssessment: 'ارزیابی',
      isAssessmentHint:
        'برچسب‌های ارزیابی میزان علاقه مخاطب را نشان می‌دهند (مانند «مشتاق» یا «بی‌علاقه») و در فهرست مخاطبین ستون جداگانه‌ای دارند.',
    },
    customFields: {
      name: 'شناسه فنی',
      nameHint: 'حروف کوچک انگلیسی و زیرخط — مثلاً congress_source',
      nameImmutable: 'شناسه فنی و نوع فیلد پس از ایجاد قابل تغییر نیستند.',
      label: 'برچسب',
      type: 'نوع',
      appliesTo: 'قابل استفاده برای',
      options: 'گزینه‌ها',
      addOption: 'افزودن گزینه',
      optionsHint: 'برای نوع «انتخابی» حداقل یک گزینه لازم است.',
      createTitle: 'فیلد سفارشی تازه',
      deactivateHint: 'غیرفعال‌سازی فیلد را از فرم‌ها پنهان می‌کند؛ مقادیر ثبت‌شده دست‌نخورده می‌مانند.',
      types: {
        TEXT: 'متن',
        NUMBER: 'عدد',
        DATE: 'تاریخ',
        BOOLEAN: 'بله/خیر',
        SELECT: 'انتخاب یکی',
        MULTISELECT: 'انتخاب چندتایی',
      },
      appliesToOptions: {
        PERSON: 'اشخاص',
        ORGANIZATION: 'سازمان‌ها',
        BOTH: 'هر دو',
      },
    },
    templates: {
      name: 'نام قالب',
      channel: 'کانال',
      category: 'دسته‌بندی',
      body: 'متن پیام',
      preview: 'پیش‌نمایش',
      createTitle: 'قالب پیام تازه',
      allowedPlaceholders: 'جایگزین‌های مجاز',
      placeholderHint: 'تنها این سه جایگزین پذیرفته می‌شوند.',
      insertPlaceholder: 'درج',
      sendingElsewhere: 'ارسال پیام در سرویس پیام‌رسانی انجام می‌شود؛ اینجا فقط متن قالب تعریف می‌شود.',
      valid: 'متن معتبر است.',
    },
    users: {
      displayName: 'نام',
      role: 'نقش',
      lastSeen: 'آخرین بازدید',
      firstSeen: 'اولین ورود',
      readOnlyNotice:
        'کاربران و نقش‌ها در Keycloak مدیریت می‌شوند. این فهرست فقط خواندنی است و پس از اولین ورود هر کاربر ساخته می‌شود.',
      openKeycloak: 'مدیریت در Keycloak',
    },
  },

  shortcuts: {
    title: 'میان‌برهای صفحه‌کلید',
    closeHint: 'برای بستن، کلید Esc یا دوباره ؟ را بزنید.',
    alwaysAvailable: 'همیشه در دسترس',
    showHelp: 'نمایش همین راهنما',
    groups: {
      general: 'عمومی',
      contacts: 'مخاطبین',
      settings: 'تنظیمات',
      session: 'نشست',
      calendar: 'تقویم',
    },
    actions: {
      searchContacts: 'جست‌وجو در مخاطبین',
      backToList: 'بازگشت به فهرست مخاطبین',
      signOut: 'خروج از حساب کاربری',
      quickAdd: 'ثبت سریع مخاطب',
      recordInteraction: 'ثبت تعامل برای این مخاطب',
      today: 'رفتن به امروز',
      prevWeek: 'هفتهٔ قبل',
      nextWeek: 'هفتهٔ بعد',
      newActivity: 'فعالیت جدید',
      followUps: 'صندوق پیگیری‌ها',
    },
  },

  /* ---------- scheduling (Step 4) ---------- */
  calendar: {
    title: 'تقویم',
    today: 'امروز',
    jumpToDate: 'رفتن به تاریخ دلخواه',
    week: 'هفته',
    day: 'روز',
    agenda: 'فهرست',
    allDay: 'تمام‌روز',
    noActivities: 'در این بازه فعالیتی ثبت نشده است',
    newActivity: 'فعالیت جدید',
    dragHint: 'برای ساختن فعالیت روی تقویم بکشید؛ برای جابه‌جایی، فعالیت را بکشید.',
    whose: 'تقویم',
    mine: 'تقویم من',
    everyone: 'همهٔ کاربران',
    syncedWithGoogle: 'همگام با گوگل',
    conflict: 'تعارض همگام‌سازی',
    conflictHint: 'این فعالیت هم اینجا و هم در گوگل تغییر کرده بود؛ نسخهٔ جدیدتر اعمال شد.',
    orphaned: 'مخاطب حذف شده',
    orphanedHint: 'مخاطب این فعالیت در بخش مخاطبین حذف شده است.',
    imported: 'واردشده از گوگل',
  },

  activity: {
    title: 'فعالیت',
    create: 'ثبت فعالیت',
    edit: 'ویرایش فعالیت',
    type: 'نوع فعالیت',
    subject: 'عنوان',
    contact: 'مخاطب',
    contactHint: 'برای یادآور شخصی خالی بگذارید',
    noContact: 'بدون مخاطب (یادآور شخصی)',
    searchContact: 'جست‌وجوی مخاطب…',
    moreContacts: (n) => `${n} مخاطب دیگر هم هست؛ برای یافتن آن‌ها جست‌وجو کنید.`,
    // «تاریخ» alone sat beside «مخاطب» and «نوع» and could have meant any of
    // them; every field on this form now says which activity it belongs to.
    date: 'تاریخ فعالیت',
    time: 'ساعت فعالیت',
    startTime: 'شروع',
    endTime: 'پایان',
    allDay: 'تمام‌روز',
    // «مسئول» was the ambiguity this form was reported for: beside «مخاطب» it
    // read as another word for the same person.
    assignee: 'مسئول فعالیت',
    assigneeHint: 'کاربری که انجام این فعالیت بر عهده اوست.',
    assigneeSelf: 'خودم',
    searchUser: 'جست‌وجوی کاربر…',
    description: 'توضیحات',
    reminders: 'یادآورها',
    addReminder: 'افزودن یادآور',
    reminderOffset: 'زمان یادآوری',
    reminderChannel: 'کانال',
    noReminders: 'یادآوری تنظیم نشده است',
    checklists: 'چک‌لیست‌ها',
    addChecklist: 'افزودن چک‌لیست',
    fromTemplate: 'از روی الگو',
    blankChecklist: 'چک‌لیست خالی',
    result: 'نتیجه',
    resultRequired: 'برای ثبت به‌عنوان انجام‌شده، نوشتن نتیجه الزامی است.',
    markDone: 'ثبت به‌عنوان انجام‌شده',
    markCancelled: 'لغو فعالیت',
    markMissed: 'ثبت به‌عنوان انجام‌نشده',
    reopen: 'بازگشایی',
    reopenReason: 'دلیل بازگشایی',
    reopenReasonHint: 'بازگشایی یک فعالیت بسته‌شده نیاز به توضیح دارد.',
    delete: 'حذف فعالیت',
    deleteConfirm: 'این فعالیت از تقویم حذف می‌شود.',
    // --- attachments on an activity ---
    attachments: 'فایل‌های پیوست',
    addFile: 'افزودن فایل',
    noFiles: 'فایلی پیوست نشده است.',
    filesAfterSave: 'فایل‌ها پس از ثبت فعالیت بارگذاری می‌شوند.',
    uploadFailed: 'فعالیت ثبت شد، اما بارگذاری برخی فایل‌ها ناموفق بود.',
    createdFromInteraction: 'پیگیری این تعامل',
    linkedActivity: 'پیگیری ثبت‌شده',
    viewActivity: 'مشاهدهٔ فعالیت',
    activitiesTab: 'فعالیت‌ها',
    noActivities: 'برای این مخاطب فعالیتی ثبت نشده است',
    reminderOffsets: {
      '10': '۱۰ دقیقه قبل',
      '30': '۳۰ دقیقه قبل',
      '60': '۱ ساعت قبل',
      '1440': '۱ روز قبل',
    },
  },

  followUps: {
    title: 'پیگیری‌ها',
    empty: 'پیگیری بازی وجود ندارد',
    emptyHint: 'وقتی فعالیتی بدون نتیجه از زمانش بگذرد، اینجا نمایش داده می‌شود.',
    accept: 'ایجاد پیگیری',
    dismiss: 'رد کردن',
    dismissed: 'ردشده',
    accepted: 'پذیرفته‌شده',
    // The composed sentence, assembled where the contact NAME is available.
    sentence: {
      OVERDUE: (name, date) => `${name} در تاریخ ${date} انجام نشده است. آیا می‌خواهید یک پیگیری جدید ایجاد کنید؟`,
      MISSED: (name, date) => `${name} در تاریخ ${date} انجام‌نشده ثبت شده است. آیا می‌خواهید یک پیگیری جدید ایجاد کنید؟`,
      CANCELLED: (name, date) => `${name} در تاریخ ${date} لغو شد و پس از آن برنامه‌ای ثبت نشده است. آیا می‌خواهید یک پیگیری جدید ایجاد کنید؟`,
      STALE: (name, date) => `از آخرین فعالیت با ${name} در تاریخ ${date} مدتی گذشته است. آیا می‌خواهید یک پیگیری جدید ایجاد کنید؟`,
    },
    reasonLabels: {
      OVERDUE: 'از زمانش گذشته',
      MISSED: 'انجام نشده',
      CANCELLED: 'لغو شده',
      STALE: 'بدون پیگیری',
    },
  },

  reminders: {
    title: 'یادآورها',
    dismiss: 'بستن',
    dismissAll: 'بستن همه',
    empty: 'یادآوری ندارید',
  },

  integrations: {
    title: 'یکپارچه‌سازی‌ها',
    google: 'تقویم و وظایف گوگل',
    notConfigured: 'پیکربندی نشده',
    notConfiguredHint:
      'برای فعال‌سازی، مدیر سامانه باید اطلاعات OAuth گوگل را در تنظیمات سرویس وارد کند. راهنما: docs/google-setup.md',
    connect: 'اتصال به گوگل',
    disconnect: 'قطع اتصال',
    disconnectConfirm: 'اتصال قطع می‌شود و همگام‌سازی متوقف می‌گردد. فعالیت‌های ثبت‌شده حذف نمی‌شوند.',
    connectedAs: 'متصل با حساب',
    lastSync: 'آخرین همگام‌سازی',
    lastError: 'آخرین خطا',
    syncNow: 'همگام‌سازی',
    syncing: 'در حال همگام‌سازی…',
    calendarTarget: 'تقویم مقصد',
    never: 'هنوز انجام نشده',
    reconnect: 'اتصال دوباره',
    recentActivity: 'رویدادهای اخیر همگام‌سازی',
    whatSyncs: 'چه چیزی همگام می‌شود',
    whatSyncsBody:
      'فعالیت‌های مش‌هاب به تقویم گوگل شما اضافه می‌شوند و تغییرات تقویم گوگل به مش‌هاب برمی‌گردد. رویدادهایی که در گوگل ساخته‌اید نیز به‌صورت فعالیت واردشده نمایش داده می‌شوند. «نتیجه» فقط در مش‌هاب نگهداری می‌شود و هرگز از سمت گوگل پاک نمی‌شود.',
    connected: 'اتصال با موفقیت انجام شد.',
    denied: 'دسترسی داده نشد.',
    error: 'اتصال ناموفق بود',
    syncResult: (pushed, pulled, imported) =>
      `${pushed} ارسال، ${pulled} دریافت، ${imported} واردشده`,
  },

  // ---------------- Step 5 ----------------

  search: {
    title: 'جست‌وجوی پیشرفته',
    filters: 'فیلترها',
    clearFilters: 'پاک کردن فیلترها',
    hideFilters: 'پنهان کردن فیلترها',
    showFilters: 'نمایش فیلترها',
    term: 'نام یا شماره',
    termHint: 'حروف عربی و فارسی و ارقام هر سه یکسان در نظر گرفته می‌شوند',
    kind: 'نوع',
    // --- the CRM profile dimensions. The titles are the ones the contact form
    // uses, so a filter and the field it filters read the same. ---
    lifecycleStage: 'وضعیت چرخه عمر مشتری',
    salesStage: 'مرحله فروش',
    customerHealth: 'وضعیت مشتری',
    engagementStatus: 'وضعیت همکاری',
    assessment: 'ارزیابی',
    referrer: 'معرف',
    referrerAny: 'همه',
    facilityType: 'نوع مخاطب',
    city: 'شهر',
    gender: 'جنسیت',
    gold: 'مشتری ویژه (Gold)',
    goldAny: 'همه',
    goldOnly: 'مشتری ویژه',
    goldRegular: 'عادی',
    fieldOfWork: 'حوزه فعالیت',
    specialty: 'تخصص',
    specialtyHint: 'انتخاب یک تخصص، زیرشاخه‌هایش را هم شامل می‌شود',
    tags: 'برچسب‌ها',
    tagMatchAny: 'هر کدام',
    tagMatchAll: 'همه',
    // Singular, matching the label the contact form uses for the same field.
    products: 'محصول مورد علاقه',
    createdRange: 'تاریخ ایجاد',
    lastInteractionRange: 'آخرین تعامل',
    noInteractionDays: 'بدون تعامل به مدت (روز)',
    affiliatedOrg: 'وابسته به سازمان',
    includeDeleted: 'شامل حذف‌شده‌ها',
    sort: 'مرتب‌سازی',
    sortName: 'نام',
    sortCreated: 'تاریخ ایجاد',
    sortLastInteraction: 'آخرین تعامل',
    sortStatus: 'وضعیت',
    results: 'نتیجه',
    savedViews: 'نماهای ذخیره‌شده',
    saveView: 'ذخیرهٔ این فیلتر',
    saveViewName: 'نام نما',
    shareView: 'اشتراک با همه (مدیر)',
    deleteView: 'حذف نما',
    systemView: 'نمای سیستمی — قابل حذف نیست',
    selectAllMatching: 'انتخاب همهٔ {n} مورد منطبق',
    selectedCount: '{n} مورد انتخاب شده',
    clearSelection: 'لغو انتخاب',
    bulkTag: 'افزودن برچسب',
    bulkUntag: 'حذف برچسب',
    bulkStatus: 'تغییر وضعیت',
    bulkMessage: 'ارسال پیام',
    bulkExport: 'خروجی CSV',
    bulkDone: '{n} مخاطب به‌روزرسانی شد',
    bulkTooLarge: 'تعداد بیش از سقف مجاز است؛ فیلتر را محدودتر کنید',
    columns: 'ستون‌ها',
    lastInteraction: 'آخرین تعامل',
    interactions: 'تعاملات',
    never: 'هرگز',
    optedOut: 'انصراف از پیام',
  },

  messaging: {
    title: 'پیام‌ها',
    outbox: 'صندوق خروجی',
    campaigns: 'کمپین‌ها',
    send: 'ارسال پیام',
    sendTo: 'ارسال به {name}',
    template: 'قالب پیام',
    freeText: 'متن آزاد',
    preview: 'پیش‌نمایش',
    force: 'ارسال با وجود هشدار',
    forceHint: 'فقط هشدار «تکراری» را نادیده می‌گیرد؛ انصراف مخاطب هرگز نادیده گرفته نمی‌شود',
    sandboxBanner: 'حالت آزمایشی — هیچ پیامی واقعاً ارسال نمی‌شود',
    liveBanner: 'اتصال واقعی به واتس‌اپ — پیام‌ها ارسال می‌شوند',
    status: 'وضعیت',
    statusQueued: 'در صف',
    statusSent: 'ارسال شد',
    statusDelivered: 'تحویل شد',
    statusRead: 'خوانده شد',
    statusFailed: 'ناموفق',
    statusSuppressed: 'ارسال نشد',
    reasonOptOut: 'انصراف مخاطب',
    // NO_CHANNEL on WhatsApp. Since 2026-09-26 a send falls back to the
    // primary phone, so this no longer means «the واتساپ box was not ticked»:
    // it means no number at all, or the gateway said the number is not on
    // WhatsApp. The old wording sent people to tick a checkbox that was never
    // the problem.
    reasonNoChannel: 'شماره‌ای ندارد یا در واتس‌اپ ثبت نشده',
    // NO_CHANNEL on an SMS: the contact has no mobile at all. «شماره واتس‌اپ
    // ندارد» beside a text message would send somebody looking for the wrong
    // number.
    reasonNoMobile: 'شماره موبایل ندارد',
    reasonDuplicate: 'تکراری',
    reasonCampaignCancelled: 'کمپین لغو شد',
    reasonQuietHours: 'در انتظار پایان ساعات سکوت',
    reasonRateLimit: 'در انتظار محدودیت ارسال',
    retry: 'تلاش دوباره',
    body: 'متن',
    recipient: 'گیرنده',
    sentAt: 'زمان ارسال',
    campaignName: 'نام کمپین',
    newCampaign: 'کمپین جدید',
    fromView: 'از نمای ذخیره‌شده',
    recipients: 'گیرندگان',
    schedule: 'زمان‌بندی',
    sendNow: 'ارسال فوری',
    cancel: 'لغو کمپین',
    counters: 'شمارنده‌ها',
    projectionLag: 'همگام‌سازی پروجکشن',
    projectionRecipients: '{n} مخاطب در پروجکشن',
    replayProjections: 'بازسازی پروجکشن‌ها',
    replayDone: '{n} مورد بازسازی شد',
    replayed: '{n} رویداد بازپخش شد',
    noMessages: 'هنوز پیامی ارسال نشده',
    // --- channel picker on the send dialog ---
    channel: 'روش ارسال',
    whatsapp: 'واتساپ',
    sms: 'پیامک',
    sendViaWhatsapp: 'ارسال از طریق واتساپ',
    sendSms: 'ارسال پیامک',
    freeBody: 'متن پیام',
    freeBodyHint:
      'اگر قالبی انتخاب نکنید، همین متن ارسال می‌شود. جایگزین‌ها برای هر مخاطب با نام او پر می‌شوند.',
    freeBodyIgnored: 'قالب انتخاب شده است؛ برای نوشتن متن آزاد، «بدون قالب» را انتخاب کنید.',
    // The placeholder chips under the free-text box. The same three the
    // template editor offers; the server substitutes them per recipient, so
    // in a batch every doctor reads their own name.
    placeholders: 'جایگزین‌ها',
    placeholderHint:
      'روی هر جایگزین بزنید تا در جای مکان‌نما درج شود؛ هنگام ارسال، برای هر مخاطب با نام همان شخص جای‌گذاری می‌شود.',
    // The SERVER decided this token is unknown (`validatePlaceholders`); the
    // client only words it. Said before sending because the renderer blanks
    // an unknown placeholder rather than leaking braces to a doctor.
    placeholderUnknown:
      'جایگزین «{token}» شناخته نمی‌شود و هنگام ارسال خالی می‌ماند. جایگزین‌های مجاز: {allowed}',
    whatsappTemplate: 'قالب پیام واتساپ',
    smsTemplate: 'قالب پیامک',
    // The deployment has no SMS route (SMS_PROVIDER=none, or the caller
    // secret is missing). Said out loud, in the place the decision is made,
    // rather than left as a button that quietly does nothing — «sandbox» and
    // «connected» must never be confusable, and neither must «sent» and «not
    // set up».
    smsNotConfigured:
      'مسیر ارسال پیامک هنوز پیکربندی نشده است. قالب و متن نهایی را می‌توانید اینجا ببینید؛ ارسال با راه‌اندازی سرویس پیامک فعال می‌شود.',
    noTemplateForChannel: 'برای این روش ارسال، قالبی تعریف نشده است.',
    // Named beside every SMS send, like the WhatsApp banner: the sandbox and
    // a real text must never look alike. The live line also says what the
    // Outbox will NOT show, so a text stuck at «ارسال شد» is not a mystery.
    smsSandbox: 'حالت آزمایشی — پیامکی واقعاً ارسال نمی‌شود.',
    smsLive: 'پیامک از خط سامانه پیامک ارسال می‌شود. تحویل پیامک گزارش نمی‌شود؛ وضعیت در «ارسال شد» می‌ماند.',
    smsBadge: 'پیامک',

    // --- sending to a selection ---
    sendToMany: 'ارسال پیام به {n} مخاطب',
    sendToManyAction: 'ساخت و ارسال کمپین',
    bulkSend: 'ارسال پیام',
    // `campaignName` already exists in this block — reused rather than
    // redeclared, because a duplicate key in one object literal silently keeps
    // the last one and the two drift apart the first time somebody edits the
    // one they can see.
    bulkDefaultName: 'ارسال گروهی — {date}',
    bulkNameHint: 'در فهرست کمپین‌ها با همین نام دیده می‌شود.',
    // A bulk send is a CAMPAIGN: the list is frozen, it can be cancelled while
    // it drains, and the Outbox accounts for it afterwards. Said on the screen
    // because «ارسال» that quietly creates a durable object is a surprise.
    bulkSendHint:
      'برای ارسال گروهی یک کمپین ساخته می‌شود: فهرست گیرندگان همان لحظه ثبت ' +
      'می‌شود، تا پیش از پایان ارسال قابل لغو است، و نتیجهٔ آن در «صندوق ' +
      'خروجی» دیده می‌شود.',
    previewSample: 'پیش‌نمایش برای {name}',
    previewSampleSuppressed:
      'برای همین مخاطب ارسال نمی‌شود؛ بقیهٔ گیرندگان جداگانه بررسی می‌شوند.',
    previewEmpty: 'هنوز متنی نوشته نشده است.',
    bulkSendQueued: 'کمپین ساخته شد؛ وضعیت ارسال در «صندوق خروجی» دیده می‌شود.',

    // --- a picture or a video ---
    mediaAdd: 'افزودن عکس یا ویدیو',
    mediaRemove: 'حذف فایل',
    mediaUploading: 'در حال بارگذاری… {pct}٪',
    mediaReady: 'آماده ارسال',
    // Text is the CAPTION, and it is optional — said beside the file, because
    // the form otherwise reads as if a message still needs words.
    mediaCaptionHint: 'متن پیام، زیرنویس این فایل می‌شود و اختیاری است.',
    // Named rather than listed as MIME types: a person reads «JPG», not
    // «image/jpeg». HEIC is called out because it is the one an iPhone makes.
    mediaWrongType:
      'فقط عکس JPG یا PNG و ویدیوی MP4 یا 3GP قابل ارسال است. عکس‌های HEIC ' +
      'آیفون را واتس‌اپ نمایش نمی‌دهد؛ آن را به JPG تبدیل کنید.',
    mediaTooLarge: 'حجم فایل بیش از {mb} مگابایت است.',
  },

  automation: {
    title: 'خودکارسازی',
    rules: 'قوانین',
    occasions: 'مناسبت‌ها',
    newRule: 'قانون جدید',
    ruleName: 'نام قانون',
    trigger: 'رویداد',
    triggerBirthday: 'تولد',
    triggerOccasion: 'مناسبت',
    triggerStatusChanged: 'تغییر مرحله اشتراک',
    triggerIssueResolved: 'حل شدن مشکل',
    sendTime: 'ساعت ارسال',
    enabled: 'فعال',
    dryRun: 'شبیه‌سازی',
    dryRunDate: 'تاریخ (میلادی)',
    dryRunHint: 'هیچ پیامی ارسال نمی‌شود — فقط نشان می‌دهد چه کسی چه چیزی می‌گرفت',
    dryRunEmpty: 'در این تاریخ کسی پیامی نمی‌گیرد',
    occasionCode: 'کد',
    occasionLabel: 'عنوان',
    jalaliMonth: 'ماه شمسی',
    jalaliDay: 'روز',
    newOccasion: 'مناسبت جدید',
    toStatus: 'وضعیت هدف',
  },

  whatsapp: {
    title: 'واتس‌اپ',
    connection: 'وضعیت اتصال',
    provider: 'ارائه‌دهنده',
    paired: 'دستگاه متصل',
    notPaired: 'دستگاهی متصل نیست',
    connected: 'آنلاین',
    disconnected: 'آفلاین',
    linkedNumber: 'شمارهٔ متصل',
    pushName: 'نام نمایشی',
    queueDepth: 'صف ارسال',
    sentLastHour: 'ارسال در یک ساعت گذشته',
    sendDisabled: 'ارسال غیرفعال است (SEND_DISABLED)',
    lastError: 'آخرین خطا',
    pair: 'اتصال دستگاه',
    pairing: 'در حال دریافت کد…',
    scanHint: 'در واتس‌اپ گوشی: تنظیمات ← دستگاه‌های متصل ← اتصال دستگاه',
    qrLabel: 'کد QR اتصال واتس‌اپ',
    qrPayloadToggle: 'کد اسکن نمی‌شود؟ نمایش متن کد',
    codeAlternative: 'یا با کد عددی متصل شوید',
    phoneForCode: 'شمارهٔ تلفن',
    pairSuccess: 'دستگاه با موفقیت متصل شد',
    pairTimeout: 'زمان به پایان رسید؛ دوباره تلاش کنید',
    unlink: 'قطع اتصال',
    unlinkConfirm: 'قطع اتصال، ارسال هر پیامی را متوقف می‌کند. مطمئنید؟',
    pacing: 'تنظیمات ارسال',
    riskTitle: 'هشدار مهم',
    riskBody:
      'مش‌هاب از پروتکل غیررسمی واتس‌اپ وب استفاده می‌کند. شمارهٔ متصل ممکن است ' +
      'محدود یا مسدود شود. از یک شمارهٔ اختصاصی استفاده کنید، حجم ارسال را ' +
      'تدریجی بالا ببرید، و انصراف مخاطبان را همیشه محترم بشمارید. ' +
      'راهنمای کامل: docs/whatsapp-setup.md',

    // --- several numbers ---
    accounts: 'شماره‌های واتس‌اپ',
    accountsEmpty: 'هنوز شماره‌ای تعریف نشده است؛ ارسال از شمارهٔ پیش‌فرض انجام می‌شود.',
    addAccount: 'افزودن شماره',
    accountLabel: 'نام شماره',
    accountLabelHint: 'نامی که کاربران هنگام ارسال می‌بینند — مثلاً «شمارهٔ فروش».',
    addAccountHint:
      'فقط یک نام لازم است. شناسهٔ فنی از روی نام ساخته می‌شود و پس از افزودن ' +
      'روی همان شماره نمایش داده می‌شود.',
    accountIdToggle: 'تعیین دستی شناسهٔ فنی (پیشرفته)',
    accountIdPlaceholder: 'خودکار ساخته می‌شود',
    accountId: 'شناسهٔ فنی',
    // The id is machinery: immutable, prefixed, and keyed to a linked device.
    // Said plainly here because the one thing a user must not do is change it
    // later expecting a rename.
    accountIdHint:
      'خالی بگذارید تا خودکار ساخته شود. فقط وقتی لازم است که بخواهید شماره ' +
      'را به نشستی که از قبل روی درگاه وجود دارد وصل کنید. پس از ساخت قابل ' +
      'تغییر نیست، چون کلید دستگاه متصل و مسیر دریافت رسیدهاست.',
    isDefault: 'پیش‌فرض',
    makeDefault: 'انتخاب به‌عنوان پیش‌فرض',
    defaultHint: 'پیام‌هایی که شماره‌ای برایشان انتخاب نشده از این شماره ارسال می‌شوند.',
    active: 'فعال',
    inactive: 'غیرفعال',
    deactivate: 'غیرفعال کردن',
    activate: 'فعال کردن',
    // The server refuses this; the client says why BEFORE the click rather
    // than rendering the refusal afterwards.
    cannotDeactivateDefault:
      'این شمارهٔ پیش‌فرض است. ابتدا شمارهٔ دیگری را پیش‌فرض کنید.',
    notLinkedYet: 'هنوز متصل نشده',
    sendsFrom: 'ارسال از',
    rename: 'تغییر نام',
    perAccountWarmup:
      'این شماره برای واتس‌اپ تازه است. حجم ارسال را چند روز اول تدریجی بالا ببرید.',
  },

  issues: {
    title: 'مشکلات و بازخوردها',
    newIssue: 'ثبت مشکل',
    issueTitle: 'عنوان',
    description: 'شرح',
    kind: 'نوع',
    kindProblem: 'مشکل',
    kindCriticism: 'انتقاد',
    kindSuggestion: 'پیشنهاد',
    kindRequest: 'درخواست',
    type: 'دسته',
    priority: 'اولویت',
    priorityLow: 'کم',
    priorityMedium: 'متوسط',
    priorityHigh: 'زیاد',
    priorityUrgent: 'فوری',
    status: 'وضعیت',
    statusOpen: 'باز',
    statusInProgress: 'در حال بررسی',
    statusWaiting: 'در انتظار مخاطب',
    statusResolved: 'حل شد',
    statusClosed: 'بسته',
    assignee: 'مسئول',
    unassigned: 'بدون مسئول',
    assignToMe: 'به من واگذار کن',
    reportedAt: 'تاریخ ثبت',
    resolution: 'نتیجه',
    resolutionRequired: 'برای «حل شد» نوشتن نتیجه الزامی است',
    reopenReason: 'دلیل بازگشایی',
    comments: 'یادداشت‌ها',
    addComment: 'افزودن یادداشت',
    internalNote: 'یادداشت داخلی',
    customerVisible: 'قابل نمایش به مخاطب',
    history: 'تاریخچه',
    links: 'پیوندها',
    orphaned: 'مخاطب حذف شده',
    notify: 'اطلاع به مخاطب',
    notified: 'در {date} اطلاع داده شد',
    notifySent: 'پیام در صف ارسال قرار گرفت',
    reports: 'گزارش‌ها',
    reportByType: 'بر اساس دسته',
    reportByKind: 'بر اساس نوع',
    reportByPriority: 'بر اساس اولویت',
    reportByAssignee: 'بر اساس مسئول',
    reportCount: 'تعداد',
    reportMedian: 'میانهٔ زمان حل (ساعت)',
    reportReopen: 'نرخ بازگشایی',
    reportBucket: 'بازهٔ زمانی',
    reportMonthly: 'ماهانه',
    reportWeekly: 'هفتگی',
    reportNone: 'بدون تفکیک',
    noIssues: 'مشکلی ثبت نشده',
    urgentUnassigned: 'مشکل فوری بدون مسئول',
  },

  checklists: {
    title: 'چک‌لیست‌ها',
    templates: 'الگوهای چک‌لیست',
    newTemplate: 'الگوی جدید',
    editTemplate: 'ویرایش الگو',
    templateTitle: 'عنوان الگو',
    templateDescription: 'توضیح',
    items: 'مراحل',
    itemsHint: 'هر خط یک مرحله',
    itemsRequired: 'یک الگو باید حداقل یک مرحله داشته باشد.',
    empty: 'هنوز الگویی تعریف نشده است',
    copyNote: 'چک‌لیستِ ساخته‌شده یک کپی است؛ ویرایش الگو، چک‌لیست‌های در جریان را تغییر نمی‌دهد.',
    addItem: 'افزودن مرحله',
    itemTitle: 'عنوان مرحله',
    done: 'انجام شد',
  },

  dashboard: {
    title: 'داشبورد مدیریت',
    subtitle: 'همهٔ اعداد از یک تعریف می‌آیند',
    // Presets. «فصل» is Farvardin-Khordad, not January-March: a quarter that
    // started in January would cut Nowruz in half.
    thisMonth: 'این ماه',
    thisQuarter: 'فصل جاری',
    thisYear: 'سال جاری',
    last30: '۳۰ روز اخیر',
    custom: 'بازهٔ دلخواه',
    bucket: 'گروه‌بندی',
    bucketDay: 'روزانه',
    bucketWeek: 'هفتگی',
    bucketMonth: 'ماهانه',
    bucketQuarter: 'فصلی',
    compare: 'مقایسه با دورهٔ قبل',
    comparedTo: 'نسبت به دورهٔ قبل',
    refresh: 'به‌روزرسانی',
    previousPeriod: 'دورهٔ قبل',
    nextPeriod: 'دورهٔ بعد',
    sections: {
      funnel: 'قیف فروش و موفقیت',
      failure: 'عوامل عدم موفقیت',
      market: 'شاخص‌های بازار',
      team: 'تیم و عملیات',
    },
    // The ⓘ. Every word of it comes from ListMetricDefinitions.
    definition: 'تعریف',
    formula: 'فرمول',
    grain: 'دانه‌بندی',
    sources: 'منابع',
    caveat: 'نکتهٔ مهم',
    // Badged on the card, so a number resting on an unconfirmed assumption
    // says so on the SCREEN and not only in a document nobody opens twice.
    needsConfirmation: 'نیازمند تأیید ذی‌نفعان',
    needsConfirmationHint:
      'این سنجه بر تصمیمی استوار است که هنوز با ذی‌نفعان نهایی نشده. ' +
      'پیش از استناد، تعریف را در docs/kpi-catalogue.md بخوانید.',
    // The FIVE conventions every number on this screen rests on. They are
    // flagged CONFIRM in docs/kpi-catalogue.md exactly like the seven
    // per-metric assumptions are — but they belong to no single metric, so
    // without this they would be the only unconfirmed decisions that never
    // reached the screen. A reader comparing «نسبت به دورهٔ قبل» has no way to
    // know what "the previous period" was chosen to mean.
    conventions: 'قراردادهای محاسبه',
    conventionsHint: 'همهٔ اعداد این صفحه بر این پنج تصمیم استوارند.',
    conventionsList: [
      'تاریخ‌ها شمسی‌اند و در منطقهٔ زمانی تهران گروه‌بندی می‌شوند.',
      '«دورهٔ قبل» بازه‌ای هم‌اندازه و بلافاصله پیش از بازهٔ انتخابی است.',
      'شمارش وضعیت فعلی، مخاطبین حذف‌شده را در بر نمی‌گیرد.',
      'انتساب به کاربر بر پایهٔ آخرین تعامل پیش از تغییر است.',
      'نرخ‌هایی با مخرج کمتر از حد کمینه نمایش داده نمی‌شوند.',
    ],
    conventionsSource: 'docs/kpi-catalogue.md',
    suppressed: 'نمونه برای محاسبه کافی نیست',
    suppressedHint:
      'نرخ‌هایی که مخرجشان از حد کمینه کمتر باشد نمایش داده نمی‌شوند. ' +
      '«۱۰۰٪» از یک مخاطب بدتر از نبودن عدد است — نقل می‌شود.',
    immature: 'هنوز به بلوغ نرسیده',
    noData: 'داده‌ای در این بازه نیست',
    metricFailed: 'این سنجه محاسبه نشد',
    drillDown: 'مشاهدهٔ فهرست',
    drillDownTitle: 'مخاطبین این عدد',
    drillDownEmpty: 'مخاطبی در این گروه نیست',
    showing: 'نمایش',
    of: 'از',
    cohortTable: 'جدول کوهورت',
    cohortMonth: 'ماه ورود',
    cohortSize: 'تعداد',
    trendView: 'روند',
    staleWarning:
      'داده‌های تحلیلی عقب است؛ اعداد این صفحه وضعیت فعلی را نشان نمی‌دهند.',
  },

  ops: {
    title: 'سلامت سامانه',
    details: 'جزئیات',
    sandbox: 'آزمایشی',
    live: 'متصل',
    services: 'سرویس‌ها',
    healthy: 'سالم',
    unhealthy: 'در دسترس نیست',
    latency: 'تأخیر پاسخ',
    address: 'نشانی',
    lag: 'تأخیر داده',
    ingest: 'دریافت رویداد',
    exchange: 'صف',
    applied: 'اعمال‌شده',
    unknown: 'ناشناخته',
    unknownHint:
      'رویدادهایی که این سرویس نمی‌شناسد. رشد این عدد یعنی سرویسی رویداد ' +
      'تازه‌ای منتشر می‌کند که کسی مصرف نمی‌کند.',
    projections: 'پروجکشن‌ها',
    recipients: 'گیرندگان',
    templates: 'قالب‌ها',
    whatsapp: 'واتس‌اپ',
    rebuild: 'بازسازی داده‌های تحلیلی',
    rebuildRunning: 'بازسازی در جریان است',
    rebuildWarning:
      'جدول‌های تحلیلی خالی می‌شوند و از نو ساخته می‌شوند. تا پایان کار ' +
      'داشبورد صفر نشان می‌دهد. عملیات دیگری مختل نمی‌شود.',
    rebuildConfirm: 'بازسازی کن',
    rebuildDone: 'بازسازی انجام شد',
    rebuildPartial:
      'بازسازی ناقص ماند. مدل خواندنی نیمه‌کاره است و اجرای دوباره بی‌خطر است.',
    replayed: 'رویدادهای بازپخش‌شده',
    audit: 'گزارش ممیزی',
    auditActor: 'کاربر',
    auditAction: 'عمل',
    auditEntity: 'موجودیت',
    auditWhen: 'زمان',
    auditService: 'سرویس',
    auditEmpty: 'رکوردی یافت نشد',
    auditFilterActor: 'فیلتر بر اساس کاربر',
    auditExport: 'خروجی CSV',
    checkedAt: 'آخرین بررسی',
  },

  // The two mobile reductions (CLAUDE.md §12). Everything else renders the
  // desktop layout on a phone, unchanged.
  mobile: {
    call: 'تماس با',
    whatsapp: 'واتس‌اپ',
    // Shown once on a phone, so the rep knows the rest of the app is there.
    desktopHint: 'بقیهٔ بخش‌ها روی نمایشگر بزرگ‌تر بهتر کار می‌کنند.',
  },

  erasure: {
    action: 'حذف کامل و بی‌نام‌سازی',
    // Two steps, because this cannot be undone and it reaches four services.
    step1Title: 'حذف کامل اطلاعات این مخاطب؟',
    step1Body:
      'این کار برگشت‌ناپذیر است. نام، شمارهٔ تماس، نشانی، تاریخ تولد و متن ' +
      'تعامل‌ها برای همیشه پاک می‌شوند. سطر مخاطب باقی می‌ماند تا تاریخچه‌ای ' +
      'که به آن اشاره دارد نشکند.',
    step1Fields: 'فیلدهایی که پاک می‌شوند',
    step1Services: 'سرویس‌هایی که تغییر می‌کنند',
    step2Title: 'برای تأیید، نام خانوادگی مخاطب را بنویسید',
    step2Hint: 'این آخرین فرصت برای انصراف است.',
    back: 'بازگشت',
    reason: 'دلیل درخواست',
    reasonHint: 'در گزارش ممیزی ثبت می‌شود. مقادیر پاک‌شده ثبت نمی‌شوند.',
    confirm: 'حذف کامل',
    cancel: 'انصراف',
    done: 'اطلاعات این مخاطب پاک شد',
    mismatch: 'نام وارد شده با نام مخاطب یکی نیست',
    onlyDeleted:
      'فقط مخاطبی که پیش‌تر حذف (نرم) شده باشد را می‌توان کامل پاک کرد.',
  },

};
